import { type ClipSegmentQuotes, locateQuotedSpan } from './clip-segment-resolver';
import { findWordSequenceIndices, type MatchableWord } from './transcript-word-match';

import { resolveClipSource } from 'js/src/components/app/chat-tab/components/ask-user-question/clip-preview';
import { retrieveTranscriptForAsset } from 'js/src/data/clip-creator/clip-creator-utils';

/** The part of a recording a tool should keep: its exact words, or the quotes it opens and closes on. */
export type SegmentRequest = { words: string[] } | { quotes: ClipSegmentQuotes };

/**
 * The segment a tool was asked for, null for the whole recording, or why the arguments don't
 * describe one.
 */
export function parseSegmentRequest({
    words,
    start_quote,
    end_quote,
}: {
    words?: string[];
    start_quote?: string;
    end_quote?: string;
}): SegmentRequest | null | { error: string } {
    if (words && words.length === 0) {
        return {
            error: `"words" was empty. Pass the transcript words of the segment to keep, or omit it to include the whole recording.`,
        };
    }
    if (words && (start_quote !== undefined || end_quote !== undefined)) {
        return { error: `pass either "words" or start_quote and end_quote, not both.` };
    }
    if ((start_quote === undefined) !== (end_quote === undefined)) {
        return {
            error: `start_quote and end_quote go together. Pass both, or neither to include the whole recording.`,
        };
    }
    if (words) {
        return { words };
    }
    return start_quote !== undefined && end_quote !== undefined
        ? { quotes: { start_quote, end_quote } }
        : null;
}

/** Where the requested segment sits in a transcript, or why it can't be found there. */
export function locateSegment(
    transcriptWords: readonly MatchableWord[],
    request: SegmentRequest,
): { startIndex: number; endIndex: number } | { error: string } {
    if ('quotes' in request) {
        const span = locateQuotedSpan(transcriptWords, request.quotes);
        return span.ok ? span : { error: span.error };
    }
    return (
        findWordSequenceIndices(transcriptWords, request.words) ?? {
            error: `"${request.words.join(' ')}" does not appear in this recording's transcript`,
        }
    );
}

/**
 * Checks the segment against the recording's own transcript before anything is added, so a
 * misquoted segment costs nothing instead of leaving the whole recording in the video. Best
 * effort: when the transcript can't be fetched yet, the check after adding still applies.
 */
export async function precheckSegment(
    mediaId: string,
    request: SegmentRequest,
): Promise<string | null> {
    const source = resolveClipSource(mediaId);
    if (!source) {
        return null;
    }
    try {
        const { transcriptWords } = await retrieveTranscriptForAsset(
            source.assetUuid,
            source.isAudio,
        );
        if (!transcriptWords || transcriptWords.length === 0) {
            return null;
        }
        const located = locateSegment(transcriptWords, request);
        return 'error' in located ? located.error : null;
    } catch {
        return null;
    }
}
